import { useCallback } from 'react';

import { MAX_POPOVER_EDIT_WIDTH } from './popover-constants';

export interface AdjustTextAreaDimensionsProps {
  textAreaRef: React.RefObject<HTMLTextAreaElement>;
  maxWidthReachedRef: React.MutableRefObject<boolean>;
  expandDirection?: 'vertical' | 'none';
  maxPopoverEditWidth?: number;
  whiteSpace?: 'nowrap' | 'normal';
}

export const useAdjustTextAreaDimensions = ({
  textAreaRef,
  maxWidthReachedRef,
  expandDirection = 'vertical',
  maxPopoverEditWidth = MAX_POPOVER_EDIT_WIDTH,
  whiteSpace = 'nowrap',
}: AdjustTextAreaDimensionsProps) => {
  return useCallback(() => {
    const textArea = textAreaRef.current;

    if (!textArea) {
      return;
    }

    textArea.style.minHeight = '100%';
    textArea.style.height = `${textArea.scrollHeight}px`;
    textArea.style.minWidth = 'auto';
    textArea.style.whiteSpace = whiteSpace;

    const textAreaWidthBuffer = 10;
    const width = Math.min(
      textArea.scrollWidth + textAreaWidthBuffer,
      maxPopoverEditWidth
    );

    if (width >= maxPopoverEditWidth && !maxWidthReachedRef.current) {
      maxWidthReachedRef.current = true;
    } else if (width < maxPopoverEditWidth && maxWidthReachedRef.current) {
      maxWidthReachedRef.current = false;
    }

    if (textArea.style.minWidth !== `${width}px`) {
      textArea.style.minWidth = `${width}px`;
    }

    // when we text wrap, max width is already reached, so the text area does
    // not need to expand any further
    if (
      expandDirection === 'vertical' &&
      (maxWidthReachedRef.current || whiteSpace === 'normal')
    ) {
      const middleOfScreen = globalThis.innerHeight / 2;
      const topOfTextArea = textArea.getBoundingClientRect().top;
      const textAreaContainerHeight = textArea.parentElement?.clientHeight || 0;
      textArea.style.whiteSpace = 'normal';
      // Text area height will retain cell height at a minimum and expand vertically
      textArea.style.height = `${Math.max(
        textAreaContainerHeight,
        textArea.scrollHeight
      )}px`;

      // Text area opens "downwards" if above middle of screen and "upwards" if below
      if (topOfTextArea > middleOfScreen) {
        textArea.style.bottom = '0px';
      } else {
        textArea.style.top = '0px';
      }
    }
  }, [expandDirection, maxPopoverEditWidth, maxWidthReachedRef, textAreaRef, whiteSpace]);
};
